import React from 'react';

// Ported from the onboarding prototype. Snapshot only at the welcome/scene
// boundary; the scene controller keeps owning the persistent carriers.
const duration = 820;
const easing = 'cubic-bezier(.22,1,.36,1)';
const MAX_FLIGHTS = 4;

type Position = {
  rect: DOMRect;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
};
type Snapshot = { pose: Position; opacity: number; visual: HTMLElement };
type VideoSnapshot = Snapshot & { id: string };
type Flight = {
  id: string;
  frame: HTMLDivElement;
  artwork: HTMLDivElement;
  destination: HTMLElement;
  visibility: string;
  animation: Animation;
  fades: Animation[];
  raf: number;
};
type Decoration = {
  frame: HTMLDivElement;
  visual: HTMLElement;
  animation: Animation;
};
type Reveal = { element: HTMLElement; animation: Animation };
type BoundarySnapshot = {
  follow?: boolean;
  videos: VideoSnapshot[];
  decorations: Snapshot[];
  reveals?: { element: HTMLElement; opacity: number }[];
};

const thumbnails = (root: HTMLElement) =>
  [
    ...root.querySelectorAll<HTMLElement>(
      '.preamble-recording[data-memory-id], [data-carrier][data-memory-id]',
    ),
  ]
    .slice(0, MAX_FLIGHTS)
    .map((card) => ({
      id: card.dataset.memoryId ?? '',
      element: card.querySelector<HTMLElement>(
        '.preamble-video-still, .carrier-image',
      ),
    }))
    .filter(
      (item): item is { id: string; element: HTMLElement } => !!item.element,
    );

function dimensions(element: HTMLElement) {
  const style = getComputedStyle(element);
  const extra = (side: string) =>
    ['padding', 'border'].reduce(
      (sum, kind) =>
        sum +
        (parseFloat(
          style.getPropertyValue(
            kind === 'padding' ? `padding-${side}` : `border-${side}-width`,
          ),
        ) || 0),
      0,
    );
  const width = parseFloat(style.width);
  const height = parseFloat(style.height);
  const borderBox = style.boxSizing === 'border-box';
  return {
    width: Number.isFinite(width)
      ? width + (borderBox ? 0 : extra('left') + extra('right'))
      : element.offsetWidth,
    height: Number.isFinite(height)
      ? height + (borderBox ? 0 : extra('top') + extra('bottom'))
      : element.offsetHeight,
  };
}
function visibleOpacity(element: HTMLElement) {
  let opacity = 1;
  for (
    let node: HTMLElement | null = element;
    node;
    node = node.parentElement
  ) {
    const style = getComputedStyle(node);
    if (style.visibility === 'hidden' || style.display === 'none') return 0;
    opacity *= Number(style.opacity);
  }
  return opacity;
}
function pose(element: HTMLElement): Position {
  const rect = element.getBoundingClientRect();
  const size = dimensions(element);
  let matrix = new DOMMatrix();
  // Origins and translations affect the center, supplied by the rect. Accumulate
  // just the linear transforms for the angle and physical unrotated dimensions.
  for (
    let node: HTMLElement | null = element;
    node;
    node = node.parentElement
  ) {
    const { transform } = getComputedStyle(node);
    if (transform !== 'none') {
      const local = new DOMMatrix(transform);
      local.e = 0;
      local.f = 0;
      matrix = local.multiply(matrix);
    }
  }
  return {
    rect,
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    width: size.width * Math.hypot(matrix.a, matrix.b),
    height: size.height * Math.hypot(matrix.c, matrix.d),
    rotation: (Math.atan2(matrix.b, matrix.a) * 180) / Math.PI,
  };
}
export function copyVisual(element: HTMLElement) {
  const clone = element.cloneNode(true) as HTMLElement;
  const originals = [element, ...element.querySelectorAll<HTMLElement>('*')];
  const copies = [clone, ...clone.querySelectorAll<HTMLElement>('*')];
  const size = dimensions(element);
  originals.forEach((original, index) => {
    const style = getComputedStyle(original);
    const copy = copies[index];
    for (let i = 0; i < style.length; i += 1) {
      const property = style[i];
      copy.style.setProperty(property, style.getPropertyValue(property));
    }
    Object.assign(copy.style, {
      pointerEvents: 'none',
      transition: 'none',
      animation: 'none',
    });
    copy.removeAttribute('id');
    if (copy.matches('button,a,input,select,textarea,[tabindex]'))
      copy.tabIndex = -1;
  });
  // Keep descendant animation opacities frozen. An interrupted crossfade
  // carries its visible composite forward.
  Object.assign(clone.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    margin: '0',
    boxSizing: 'border-box',
    width: `${size.width}px`,
    height: `${size.height}px`,
    transform: 'none',
    transformOrigin: '0 0',
    visibility: 'visible',
    opacity: '1',
  });
  return clone;
}
function capture(
  element: HTMLElement,
  visualElement: HTMLElement = element,
): Snapshot {
  return {
    pose: pose(element),
    opacity: visibleOpacity(element),
    visual: copyVisual(visualElement),
  };
}
const inViewport = (rect: DOMRect, viewport: DOMRect) =>
  rect.right > viewport.left &&
  rect.left < viewport.right &&
  rect.bottom > viewport.top &&
  rect.top < viewport.bottom;
const transform = (
  position: Pick<Position, 'x' | 'y' | 'width' | 'height' | 'rotation'>,
  viewport: DOMRect,
  width: number,
  height: number,
) =>
  `translate3d(${position.x - viewport.left}px,${position.y - viewport.top}px,0) rotate(${position.rotation}deg) scale(${position.width / width},${position.height / height}) translate(-50%,-50%)`;
const shortestTurn = (from: number, to: number) =>
  from + ((to - from + 540) % 360) - 180;
function fitVisual(visual: HTMLElement, width: number, height: number) {
  // These border-box dimensions were frozen before detaching the snapshot.
  // eslint-disable-next-line no-param-reassign
  visual.style.transform = `scale(${width / parseFloat(visual.style.width)},${height / parseFloat(visual.style.height)})`;
}
function supportingElements(
  root: HTMLElement,
  page: number,
  flyingIds: Set<string>,
) {
  if (page === 0)
    return [...root.querySelectorAll<HTMLElement>('.preamble-recording')];
  return [...root.querySelectorAll<HTMLElement>('[data-carrier]')]
    .filter((card) => !flyingIds.has(card.dataset.memoryId ?? ''))
    .map((card) => card.querySelector<HTMLElement>('.carrier-image'))
    .filter((element): element is HTMLElement => !!element);
}

type Props = { page: number; children: React.ReactNode };

export class WelcomeMemoryMotion extends React.Component<Props> {
  root = React.createRef<HTMLDivElement>();

  content = React.createRef<HTMLDivElement>();

  overlay = React.createRef<HTMLDivElement>();

  flights: Flight[] = [];

  decorations: Decoration[] = [];

  reveals: Reveal[] = [];

  preference: MediaQueryList | null = null;

  observer: ResizeObserver | null = null;

  viewport: DOMRect | null = null;

  componentDidMount() {
    this.preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.preference.addEventListener('change', this.stop);
    window.addEventListener('resize', this.stop);
    if (typeof ResizeObserver !== 'undefined' && this.root.current) {
      this.observer = new ResizeObserver(() => {
        const rect = this.root.current!.getBoundingClientRect();
        if (
          this.viewport &&
          (rect.width !== this.viewport.width ||
            rect.height !== this.viewport.height)
        )
          this.stop();
        this.viewport = rect;
      });
      this.observer.observe(this.root.current);
    }
  }

  getSnapshotBeforeUpdate(previous: Props): BoundarySnapshot | null {
    const { page } = this.props;
    if (previous.page === page) return null;
    const crossing =
      (previous.page === 0 && page === 1) ||
      (previous.page === 1 && page === 0);
    const handoff =
      this.flights.length > 0 &&
      previous.page >= 1 &&
      previous.page <= 3 &&
      page >= 1 &&
      page <= 3;
    const content = this.content.current;
    const supported =
      typeof content?.animate === 'function' &&
      typeof DOMMatrix !== 'undefined';
    let snapshot: BoundarySnapshot | null = null;
    if ((crossing || handoff) && supported && !this.preference?.matches) {
      if (handoff) {
        snapshot = {
          follow: true,
          videos: this.flights.map((flight) => ({
            id: flight.id,
            ...capture(flight.frame, flight.artwork),
          })),
          decorations: this.decorations.map((item) =>
            capture(item.frame, item.visual),
          ),
          reveals: this.reveals.map(({ element }) => ({
            element,
            opacity: Number(getComputedStyle(element).opacity),
          })),
        };
      } else if (content && this.root.current) {
        const viewport = this.root.current.getBoundingClientRect();
        const videos: VideoSnapshot[] = [];
        thumbnails(content).forEach(({ id, element }) => {
          const ongoing = this.flights.find((flight) => flight.id === id);
          const source = ongoing?.frame || element;
          if (!inViewport(source.getBoundingClientRect(), viewport)) return;
          videos.push({ id, ...capture(source, ongoing?.artwork || element) });
        });
        const flyingIds = new Set(videos.map((video) => video.id));
        // Scene captions stay with the scene controller, never the ghosts.
        const decorations = this.decorations.map((item) =>
          capture(item.frame, item.visual),
        );
        supportingElements(content, previous.page, flyingIds).forEach(
          (element) => {
            if (
              visibleOpacity(element) <= 0.001 ||
              !inViewport(element.getBoundingClientRect(), viewport)
            )
              return;
            const decoration = capture(element);
            if (
              element.matches('.preamble-recording') &&
              flyingIds.has(element.dataset.memoryId ?? '')
            ) {
              const still = decoration.visual.querySelector<HTMLElement>(
                '.preamble-video-still',
              );
              if (still) still.style.opacity = '0';
            }
            decorations.push(decoration);
          },
        );
        snapshot = { videos, decorations };
      }
    }
    this.stop();
    return snapshot;
  }

  componentDidUpdate(
    _previous: Props,
    _state: unknown,
    snapshot: BoundarySnapshot | null,
  ) {
    if (!this.root.current || !this.content.current) return;
    this.viewport = this.root.current.getBoundingClientRect();
    if (!snapshot) return;
    const content = this.content.current;
    const viewport = this.root.current.getBoundingClientRect();
    const targets = thumbnails(content);
    const flyingIds = new Set<string>();
    snapshot.videos.forEach((video) => {
      const destination = targets.find((item) => item.id === video.id)?.element;
      if (!destination?.animate) return;
      const end = pose(destination);
      const { width, height } = video.pose;
      if (!end.width || !end.height) return;
      const frame = this.createFrame(video, viewport);
      if (!frame) return;
      frame.dataset.memoryId = video.id;
      flyingIds.add(video.id);
      // Identical center/rotation/scale lists interpolate the angle instead of
      // flattening it. Choose the shortest turn from the currently visible pose.
      end.rotation = shortestTurn(video.pose.rotation, end.rotation);
      const artwork = document.createElement('div');
      Object.assign(artwork.style, {
        position: 'absolute',
        inset: '0',
        width: `${width}px`,
        height: `${height}px`,
      });
      artwork.append(video.visual);
      frame.append(artwork);
      const destinationVisual = copyVisual(destination);
      fitVisual(destinationVisual, width, height);
      destinationVisual.style.opacity = '0';
      artwork.append(destinationVisual);
      const { visibility } = destination.style;
      destination.style.visibility = 'hidden';
      const keyframes = snapshot.follow
        ? [{ opacity: video.opacity }, { opacity: 1 }]
        : [
            {
              transform: transform(video.pose, viewport, width, height),
              opacity: video.opacity,
            },
            { transform: transform(end, viewport, width, height), opacity: 1 },
          ];
      const animation = frame.animate(keyframes, {
        duration,
        easing,
        fill: 'both',
      });
      const fadeTiming: KeyframeAnimationOptions = {
        duration,
        easing: 'linear',
        fill: 'both',
      };
      const fadeStart = 0.64;
      const fades = [
        video.visual.animate(
          [
            { opacity: 1, offset: 0 },
            { opacity: 1, offset: fadeStart, easing },
            { opacity: 0, offset: 1 },
          ],
          fadeTiming,
        ),
        destinationVisual.animate(
          [
            { opacity: 0, offset: 0 },
            { opacity: 0, offset: fadeStart, easing },
            { opacity: 1, offset: 1 },
          ],
          fadeTiming,
        ),
      ];
      fades.forEach((fade) => fade.finished.catch(() => {}));
      const flight: Flight = {
        id: video.id,
        frame,
        artwork,
        destination,
        visibility,
        animation,
        fades,
        raf: 0,
      };
      this.flights.push(flight);
      if (snapshot.follow) {
        // Only a rapid departure from the welcome bridge needs a moving target.
        // Follow the controller's real pose while the ghost converges to it.
        const started = performance.now();
        const tick = (now: number) => {
          if (!this.flights.includes(flight)) return;
          const progress = Math.min(1, (now - started) / duration);
          const amount = 1 - (1 - progress) ** 3;
          const current = pose(destination);
          current.rotation = shortestTurn(
            video.pose.rotation,
            current.rotation,
          );
          const next = {
            x: video.pose.x + (current.x - video.pose.x) * amount,
            y: video.pose.y + (current.y - video.pose.y) * amount,
            width:
              video.pose.width + (current.width - video.pose.width) * amount,
            height:
              video.pose.height + (current.height - video.pose.height) * amount,
            rotation:
              video.pose.rotation +
              (current.rotation - video.pose.rotation) * amount,
          };
          frame.style.transform = transform(next, viewport, width, height);
          if (progress < 1) flight.raf = requestAnimationFrame(tick);
        };
        flight.raf = requestAnimationFrame(tick);
      }
      animation.finished.then(
        () => {
          if (!this.flights.includes(flight)) return;
          cancelAnimationFrame(flight.raf);
          destination.style.visibility = visibility;
          frame.remove();
          fades.forEach((fade) => fade.cancel());
          this.flights = this.flights.filter((item) => item !== flight);
        },
        () => {},
      );
    });
    snapshot.decorations.forEach((item) => {
      if (item.opacity <= 0.001) return;
      const frame = this.createFrame(item, viewport, true);
      if (!frame) return;
      const animation = frame.animate(
        [{ opacity: item.opacity }, { opacity: 0 }],
        { duration: 460, easing, fill: 'both' },
      );
      const decoration = { frame, visual: item.visual, animation };
      this.decorations.push(decoration);
      animation.finished.then(
        () => {
          frame.remove();
          this.decorations = this.decorations.filter(
            (entry) => entry !== decoration,
          );
        },
        () => {},
      );
    });
    if (snapshot.follow) {
      // Continue any illustration reveal from its visible alpha.
      snapshot.reveals?.forEach((item) => {
        if (item.element.isConnected)
          this.reveal(item.element, [
            { opacity: item.opacity },
            { opacity: 1 },
          ]);
      });
      return;
    }
    // Animate leaves so scene root and carrier opacity remain entirely under
    // the persistent controller's control after this boundary.
    supportingElements(content, this.props.page, flyingIds).forEach(
      (element) => {
        if (!element.animate || visibleOpacity(element) <= 0.001) return;
        const opacity = Number(getComputedStyle(element).opacity);
        this.reveal(element, [
          { opacity: 0 },
          { opacity: 0, offset: 0.12 },
          { opacity },
        ]);
      },
    );
  }

  componentWillUnmount() {
    this.stop();
    this.observer?.disconnect();
    this.preference?.removeEventListener('change', this.stop);
    window.removeEventListener('resize', this.stop);
  }

  stop = () => {
    this.flights.forEach((flight) => {
      cancelAnimationFrame(flight.raf);
      flight.animation.cancel();
      flight.fades.forEach((fade) => fade.cancel());
      // eslint-disable-next-line no-param-reassign
      flight.destination.style.visibility = flight.visibility;
      flight.frame.remove();
    });
    this.decorations.forEach((decoration) => {
      decoration.animation.cancel();
      decoration.frame.remove();
    });
    this.reveals.forEach((reveal) => reveal.animation.cancel());
    this.flights = [];
    this.decorations = [];
    this.reveals = [];
  };

  createFrame(snapshot: Snapshot, viewport: DOMRect, decoration = false) {
    const { width, height } = snapshot.pose;
    if (!width || !height || !this.overlay.current) return null;
    const frame = document.createElement('div');
    frame.className = 'gs-memory-flight';
    frame.setAttribute('aria-hidden', 'true');
    frame.inert = true;
    Object.assign(frame.style, {
      width: `${width}px`,
      height: `${height}px`,
      transform: transform(snapshot.pose, viewport, width, height),
      transformOrigin: '0 0',
      pointerEvents: 'none',
      overflow: decoration ? 'visible' : 'hidden',
      zIndex: decoration ? '0' : '1',
    });
    fitVisual(snapshot.visual, width, height);
    frame.append(snapshot.visual);
    this.overlay.current.append(frame);
    return frame;
  }

  reveal(element: HTMLElement, keyframes: Keyframe[]) {
    const animation = element.animate(keyframes, {
      duration,
      easing,
      fill: 'both',
    });
    const reveal = { element, animation };
    this.reveals.push(reveal);
    animation.finished.then(
      () => {
        animation.cancel();
        this.reveals = this.reveals.filter((item) => item !== reveal);
      },
      () => {},
    );
  }

  render() {
    const { children } = this.props;
    return (
      <div ref={this.root} className="welcome-memory-motion">
        <div ref={this.content} className="welcome-memory-content">
          {children}
        </div>
        <div
          ref={this.overlay}
          className="gs-memory-flights"
          aria-hidden="true"
        />
      </div>
    );
  }
}
